/**
 * atom-cart-quick.js — Header basket bubble updater.
 *
 * Listens for the theme's cartUpdate pubsub event (fired by Dawn's
 * <product-form> after a successful AJAX add to /cart/add.js), then
 * fetches /cart.js and writes the customer-facing item count into the bubble.
 *
 * Why this is needed:
 * - We hide <cart-notification> via CSS so users only see the badge
 *   tick up — no popup, no redirect to /cart.
 * - The bubble in atom-site-header.liquid is server-rendered for the
 *   first paint. After that, this script keeps it in sync.
 *
 * Animation: adds .is-bumped to trigger the pop keyframe defined in the
 * homepage split CSS. Class strips itself after 320ms.
 *
 * Reduced motion: respected via the CSS @media rule, no JS branch needed.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  function customerFacingCartCount(cart) {
    if (!cart || typeof cart.item_count !== 'number') return 0;
    var count = cart.item_count;
    // Globo merged display: hidden companion add-on lines (e.g. extension cables)
    // are folded into their parent row, so exclude them from the visible count.
    // Orphan companions (parent removed server-side) still count — never invisible.
    if (Array.isArray(cart.items)) {
      var parentGroups = {};
      cart.items.forEach(function (it) {
        var g = it.properties && it.properties._gpo_product_group;
        if (g) parentGroups[g] = true;
      });
      cart.items.forEach(function (it) {
        var pg = it.properties && it.properties._gpo_parent_product_group;
        if (pg && parentGroups[pg]) count -= it.quantity;
      });
    }
    return Math.max(0, count);
  }

  window.atomCustomerFacingCartCount = customerFacingCartCount;

  // ─── Bubble update helper ──────────────────────────────────────────────
  function setBubbleCount(count) {
    var bubbles = document.querySelectorAll('[data-cart-count]');
    var aria = document.querySelectorAll('[data-cart-count-aria]');
    var n = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;

    bubbles.forEach(function (el) {
      var prev = parseInt(el.textContent, 10) || 0;
      // Render: empty string when 0 so :empty CSS hides it
      el.textContent = n > 0 ? String(n) : '';
      // Pop animation only when count actually changed and is > 0
      if (n !== prev && n > 0) {
        el.classList.remove('is-bumped');
        // Force reflow so the animation re-triggers
        void el.offsetWidth;
        el.classList.add('is-bumped');
        setTimeout(function () {
          el.classList.remove('is-bumped');
        }, 360);
      }
    });

    aria.forEach(function (el) {
      el.textContent = n + (n === 1 ? ' item' : ' items') + ' in basket';
    });
  }

  // ─── Fetch current cart and update bubble ──────────────────────────────
  function refreshFromCart() {
    return fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart fetch ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        setBubbleCount(customerFacingCartCount(cart));
      })
      .catch(function (err) {
        // Non-fatal: leave the existing server-rendered count in place
        console.warn('[atom-cart-quick] refresh failed:', err && err.message);
      });
  }

  // ─── Wire to Dawn's pub-sub if present, else fall back to form intercept ──
  function wirePubSub() {
    if (
      typeof window.subscribe === 'function' &&
      window.PUB_SUB_EVENTS &&
      typeof window.PUB_SUB_EVENTS.cartUpdate === 'string'
    ) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {
        refreshFromCart();
      });
      if (typeof window.PUB_SUB_EVENTS.cartError === 'string') {
        window.subscribe(window.PUB_SUB_EVENTS.cartError, function () {
          // Refresh anyway — count may be stale after an error
          refreshFromCart();
        });
      }
      return true;
    }
    return false;
  }

  // ─── Fallback: direct fetch interceptor on /cart/add ───────────────────
  // If pub-sub is unavailable for any reason, hook XMLHttpRequest + fetch
  // so we still pick up cart-add events.
  function wireFetchFallback() {
    var origFetch = window.fetch;
    if (typeof origFetch !== 'function') return;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
        p.then(function (r) {
          if (r && r.ok) refreshFromCart();
        }).catch(function () {});
      }
      return p;
    };
  }

  // ─── Fast checkout from the add-to-cart notification ─────────────────
  // The notification checkout form otherwise posts to /cart first. Sending
  // the shopper straight to /checkout removes an avoidable revenue-path hop.
  function wireNotificationCheckout() {
    var warmed = false;
    var navigating = false;
    var selector = '[data-cart-notification-checkout]';

    function warmCheckoutConnections() {
      if (warmed) return;
      warmed = true;

      [
        'https://cdn.shopify.com',
        'https://shop.app',
        'https://pay.google.com',
        'https://www.paypal.com',
        'https://c.paypal.com',
      ].forEach(function (origin) {
        if (document.querySelector('link[rel="preconnect"][href="' + origin + '"]')) return;
        var link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = origin;
        if (origin === 'https://cdn.shopify.com') link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      });
    }

    function setBusy(button, commit) {
      if (!button) return;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      if (!commit) return;
      if (!button.getAttribute('data-checkout-label')) {
        button.setAttribute('data-checkout-label', button.textContent.trim());
      }
      button.textContent = 'Opening checkout...';
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    }

    function clearBusy() {
      document.querySelectorAll(selector).forEach(function (button) {
        var label = button.getAttribute('data-checkout-label');
        if (label) button.textContent = label;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
        button.disabled = false;
      });
    }

    function getButton(event) {
      return event.target && event.target.closest ? event.target.closest(selector) : null;
    }

    function openCheckout(button) {
      if (navigating) return;
      navigating = true;
      warmCheckoutConnections();
      setBusy(button, true);
      window.location.assign('/checkout');
    }

    document.addEventListener('pointerenter', function (event) {
      if (getButton(event)) warmCheckoutConnections();
    }, true);

    document.addEventListener('focusin', function (event) {
      if (getButton(event)) warmCheckoutConnections();
    });

    document.addEventListener('pointerdown', function (event) {
      var button = getButton(event);
      if (!button) return;
      warmCheckoutConnections();
      setBusy(button, false);
    }, true);

    document.addEventListener('touchstart', function (event) {
      var button = getButton(event);
      if (!button) return;
      warmCheckoutConnections();
      setBusy(button, false);
    }, { capture: true, passive: true });

    document.addEventListener('click', function (event) {
      var button = getButton(event);
      if (!button) return;
      event.preventDefault();
      openCheckout(button);
    }, true);

    document.addEventListener('submit', function (event) {
      if (event.target && event.target.id !== 'cart-notification-form') return;
      var button = event.submitter || document.querySelector(selector);
      if (!button || !button.matches(selector)) return;
      event.preventDefault();
      openCheckout(button);
    }, true);

    window.addEventListener('pageshow', function (event) {
      navigating = false;
      clearBusy();
      if (event.persisted) refreshFromCart();
    });

    window.addEventListener('pagehide', clearBusy);
  }

  // ─── Intercept native form submits to /cart/add ────────────────────────
  // Without a <cart-notification> or <cart-drawer> in the DOM, Dawn's
  // <product-form> custom element falls back to a native submit, which
  // redirects the browser to /cart. We capture every submit on a form
  // whose action targets /cart/add, prevent the default, and run it as
  // an AJAX POST to /cart/add.js so the user stays on the page.
  function wireFormInterceptor() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;

      var action = (form.getAttribute('action') || form.action || '').toLowerCase();
      // Match /cart/add (with or without leading domain, with or without .js)
      if (!/\/cart\/add(\.js)?(\?|$|\#)/.test(action)) return;

      // Allow opt-out per form (e.g. if a future surface needs the redirect)
      if (form.dataset && form.dataset.bypassQuickCart === 'true') return;

      if (typeof form.__atomSubmitConfiguredProduct === 'function') {
        e.preventDefault();
        e.stopImmediatePropagation();
        form.__atomSubmitConfiguredProduct(e);
        return;
      }

      if (form.dataset.atomQuickCartBusy === 'true') {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      // If Dawn's product-form custom element handled it (because
      // cart-notification/cart-drawer was present), the submit's already
      // prevented and we don't need to do anything. Detect that by the
      // form being inside an `is="product-form"` or `<product-form>` that
      // already set its loading state.
      if (form.classList.contains('is-loading')) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      form.dataset.atomQuickCartBusy = 'true';
      var attemptGeneration = (parseInt(form.dataset.atomQuickCartGeneration, 10) || 0) + 1;
      form.dataset.atomQuickCartGeneration = String(attemptGeneration);

      var submitButton =
        form.querySelector('[type="submit"]:not([disabled])') ||
        document.activeElement;
      var buttonWasDisabled = Boolean(submitButton && submitButton.disabled);
      var buttonAriaDisabled = submitButton && submitButton.getAttribute('aria-disabled');
      var buttonAriaBusy = submitButton && submitButton.getAttribute('aria-busy');
      var buttonAriaLabel = submitButton && submitButton.getAttribute('aria-label');
      var committedUiShown = false;
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.setAttribute('aria-busy', 'true');
        submitButton.classList.add('loading');
      }

      function releaseForm() {
        if (form.dataset.atomQuickCartGeneration !== String(attemptGeneration)) return;
        delete form.dataset.atomQuickCartBusy;
        delete form.dataset.atomQuickCartUncertain;
        if (!submitButton || !submitButton.classList) return;
        submitButton.disabled = buttonWasDisabled;
        if (buttonAriaDisabled === null) submitButton.removeAttribute('aria-disabled');
        else submitButton.setAttribute('aria-disabled', buttonAriaDisabled);
        if (buttonAriaBusy === null) submitButton.removeAttribute('aria-busy');
        else submitButton.setAttribute('aria-busy', buttonAriaBusy);
        if (buttonAriaLabel === null) submitButton.removeAttribute('aria-label');
        else submitButton.setAttribute('aria-label', buttonAriaLabel);
        submitButton.classList.remove('loading', 'is-added');
      }

      function holdUncertain() {
        if (form.dataset.atomQuickCartGeneration !== String(attemptGeneration)) return;
        form.dataset.atomQuickCartUncertain = 'true';
        if (!submitButton || !submitButton.classList) return;
        submitButton.classList.remove('loading');
        submitButton.setAttribute('aria-busy', 'false');
        submitButton.setAttribute('aria-label', 'Check basket before retrying');
        submitButton.title = 'Check basket before retrying';
      }

      function showCommitted() {
        if (form.dataset.atomQuickCartGeneration !== String(attemptGeneration) || committedUiShown) return;
        committedUiShown = true;
        if (submitButton && submitButton.classList) {
          submitButton.classList.remove('loading');
          submitButton.classList.add('is-added');
          submitButton.setAttribute('aria-busy', 'false');
          submitButton.setAttribute('aria-label', 'Added to basket');
        }
        setTimeout(releaseForm, 1200);
      }

      var formData;
      try {
        formData = new FormData(form);
      } catch (formError) {
        releaseForm();
        console.error('[atom-cart-quick] could not build cart request:', formError);
        return;
      }
      // Always set explicit accept so Shopify returns JSON not HTML
      var headers = { Accept: 'application/javascript' };
      var addCommitted = false;
      var addAmbiguous = false;

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: headers,
        credentials: 'same-origin',
      })
        .then(function (r) {
          addCommitted = r.ok;
          addAmbiguous = !r.ok && (r.status >= 500 || r.status === 408);
          if (addCommitted) showCommitted();
          return r.text().then(function (text) {
            var json = null;
            if (text) {
              try { json = JSON.parse(text); }
              catch (_parseError) { /* A 2xx response is committed even if its body is unreadable. */ }
            }
            return { ok: r.ok, status: r.status, json: json };
          });
        })
        .then(function (res) {
          if (!res.ok) {
            console.warn('[atom-cart-quick] cart/add.js error:', res.status, res.json);
            if (addAmbiguous) {
              holdUncertain();
              return;
            }
            refreshFromCart();
            releaseForm();
            return;
          }
          refreshFromCart();
        })
        .catch(function (err) {
          console.error('[atom-cart-quick] form intercept failed:', err);
	          if (addCommitted) {
	            showCommitted();
	            return;
	          }
	          holdUncertain();
        });
    }, true /* useCapture — fire before any other handler */);
  }

  // ─── Optional: intercept the Basket icon click when cart is empty ──────
  // We do NOT swallow the click on /cart navigation — clicking the basket
  // icon should still take you to the cart page. The user's request is
  // about the ADD action, not the basket icon itself.

  // ─── Bootstrap ─────────────────────────────────────────────────────────
  function init() {
    var pubsubReady = wirePubSub();
    if (!pubsubReady) {
      // Re-try once Dawn's pubsub.js loads (it's deferred)
      var attempts = 0;
      var iv = setInterval(function () {
        attempts++;
        if (wirePubSub() || attempts > 40) clearInterval(iv);
      }, 50);
    }

    wireFetchFallback();
    wireNotificationCheckout();
    wireFormInterceptor();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
